/*
 *  KAN_Logo3D.js
 *  Project: Kanoogi Platform — home-page 3D logo
 *
 *  Loads /models/kanoogi_logo.obj + .mtl and renders it in 3D as a drop-in
 *  replacement for the static SVG <img>. Platform class (docs/webgpu-conversion-plan.md
 *  §0, §7), so three tiers, tried in order:
 *      1. WebGPU  - asked for first; its device request overlaps the model fetch
 *      2. WebGL   - the pre-WebGPU renderer, kept as the fallback. Only probed when
 *                   WebGPU has failed, so a WebGPU visitor never makes a WebGL context
 *      3. the <img> - left in place (revealed), silently, if both fail or the model
 *                   will not load
 *  opts.webgpuOnly skips tier 2: the game's BEGIN modal is game class, not platform.
 *  Only the shaders, buffer upload and draw calls differ between the tiers; they live in
 *  KAN_Logo3DGPU.js. Everything else here is shared.
 *
 *  Public API:
 *      InitLogo3D(imgElement, opts) -> Promise<handle | false>
 *          handle : canvas replaced the <img>, render loop running.
 *                   {tier:'webgpu'|'webgl', renderer, canvas, Dispose()}.
 *                   Dispose() stops the loop, removes the canvas and frees the GPU
 *                   (the last renderer on the page's shared WebGPU device destroys it).
 *          false  : <img> left in place
 *      Callers test truthiness, so a handle reads exactly as the old `true` did.
 *
 *  Created by Chris Taylor (with Claude) on 2026-05-13
 *  Copyright © 2026 Kanoogi Inc. | All Rights Reserved
 */

"use strict";

import { mat4 } from 'gl-matrix';
import { RequestWebGPUDevice } from './KAN_WebGPUSupport.js';
import { CreateWebGLBackend, CreateWebGPUBackend, FrameValues, NewLighting } from './KAN_Logo3DGPU.js';

/* ---------- WebGL availability probe ---------- */

// LoseContext(gl) - release a WebGL context NOW. Browsers cap how many live WebGL
// contexts a page may hold and silently kill the OLDEST one when the cap is passed, so
// leaving a dead context to GC can take a live logo down with it.
function LoseContext(gl)
	{
	try
		{
		const lose=gl&&gl.getExtension('WEBGL_lose_context');
		if (lose) lose.loseContext();
		}
	catch (_) { /* already lost */ }
	}

function ProbeWebGL()
	{
	try
		{
		const c=document.createElement('canvas');
		const gl=c.getContext('webgl')||c.getContext('experimental-webgl');
		if (!gl) return false;
		const s=gl.createShader(gl.VERTEX_SHADER);
		gl.shaderSource(s,'void main(){gl_Position=vec4(0.0);}');
		gl.compileShader(s);
		const ok=gl.getShaderParameter(s,gl.COMPILE_STATUS);
		gl.deleteShader(s);
		// Release the probe's context explicitly. Leaving this to GC meant every
		// InitLogo3D call leaked a context for an indeterminate time -- home
		// creates two renderers (logo + tagline), so the page carried 4 contexts
		// where 2 would do, and the oldest to die was the logo's.
		LoseContext(gl);
		return !!ok;
		}
	catch (_)
		{
		return false;
		}
	}

/* ---------- WebGPU device: one per page ---------- */

// Home runs two renderers (logo + tagline), so they share ONE lazily created device rather
// than holding two. Reference counted: the last Dispose() destroys it, and the next
// InitLogo3D starts a fresh one. A failed request is remembered for the page - a second
// renderer must not sit through the same timeout again - but a LOST device is forgotten,
// so a later init can try once more.
//
// The deadline is short because the placeholder is CSS-hidden while we wait
// (KanoogiMain/src/style.css, GSMProx/src/css/mainsite.css: img.kan-logo3d-target), and
// the 8 s ready-watchdog is only armed once a canvas exists. A hung requestAdapter or
// requestDevice must FALL THROUGH to WebGL, never leave the logo invisible.
export const LOGO_GPU_TIMEOUT_MS=3000;

const gpuShare={promise:null,device:null,refs:0};

function HasWebGPU()
	{
	return(typeof navigator!=='undefined'&&!!navigator.gpu);
	}

async function AcquireLogoGPU(timeoutMs)
	{
	for (let attempt=0;attempt<3;attempt++)
		{
		if (!gpuShare.promise)
			{
			const p=RequestWebGPUDevice({timeoutMs}).then((r)=>
				{
				if (r.ok)
					{
					gpuShare.device=r.device;
					r.device.lost.then(()=>
						{
						if (gpuShare.promise===p) { gpuShare.promise=null; gpuShare.device=null; gpuShare.refs=0; }
						});
					}
				return(r);
				});
			gpuShare.promise=p;
			}
		const p=gpuShare.promise;
		const r=await p;
		if (!r.ok) return(r);
		if (gpuShare.promise!==p) continue;						// released or lost while we waited: that device is gone, ask again
		gpuShare.refs++;
		return(r);
		}
	return({ok:false,reason:'device_failed',detail:'the shared device kept going away'});
	}

function ReleaseLogoGPU(device)
	{
	if (!device||device!==gpuShare.device) return;				// already forgotten (lost)
	if (--gpuShare.refs>0) return;
	gpuShare.promise=null;
	gpuShare.device=null;
	gpuShare.refs=0;
	try { device.destroy(); } catch (_) { /* already gone */ }
	}

/* ---------- OBJ + MTL parsing ---------- */

async function FetchText(url)
	{
	const r=await fetch(url);
	if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
	return await r.text();
	}

function ParseMTL(text)
	{
	const mats={};
	let cur=null;
	const lines=text.split('\n');
	for (let i=0;i<lines.length;i++)
		{
		const line=lines[i].trim();
		if (!line||line[0]==='#') continue;
		const p=line.split(/\s+/);
		if (p[0]==='newmtl')
			{
			cur=p[1];
			mats[cur]={Kd:[0.8,0.8,0.8]};
			}
		else if (p[0]==='Kd'&&cur)
			{
			mats[cur].Kd=[parseFloat(p[1]),parseFloat(p[2]),parseFloat(p[3])];
			}
		}
	return mats;
	}

function ParseOBJ(text)
	{
	const positions=[];												// flat [x,y,z,...]
	const vertMat=[];												// material name per vertex
	const faceIdx=[];												// flat [a,b,c, a,b,c, ...] 0-based
	const objects=[];												// per-`o` metadata: {name, indexStart, indexCount, vStart, vEnd}
	let curMat='default';
	let mtllib=null;
	let curObj=null;
	const lines=text.split('\n');
	for (let i=0;i<lines.length;i++)
		{
		const line=lines[i].trim();
		if (!line||line[0]==='#') continue;
		const p=line.split(/\s+/);
		switch (p[0])
			{
			case 'mtllib': mtllib=p[1]; break;
			case 'usemtl': curMat=p[1]; break;
			case 'o':
				if (curObj)
					{
					curObj.indexCount=faceIdx.length-curObj.indexStart;
					curObj.vEnd=positions.length/3;
					}
				curObj={name:p[1],indexStart:faceIdx.length,indexCount:0,vStart:positions.length/3,vEnd:0};
				objects.push(curObj);
				break;
			case 'v':
				positions.push(parseFloat(p[1]),parseFloat(p[2]),parseFloat(p[3]));
				vertMat.push(curMat);
				break;
			case 'f':
				{
				const vi=[];
				for (let k=1;k<p.length;k++) vi.push(parseInt(p[k].split('/')[0],10)-1);
				if (vi.length===3)
					{
					faceIdx.push(vi[0],vi[1],vi[2]);
					}
				else if (vi.length>3)
					{
					for (let k=1;k<vi.length-1;k++)
						faceIdx.push(vi[0],vi[k],vi[k+1]);
					}
				break;
				}
			}
		}
	if (curObj)
		{
		curObj.indexCount=faceIdx.length-curObj.indexStart;
		curObj.vEnd=positions.length/3;
		}
	return {positions,vertMat,faceIdx,objects,mtllib};
	}

/* ---------- Mesh build (smooth normals + per-vertex color) ---------- */

function BuildMesh(parsed,mats,colorMap)
	{
	const N=parsed.positions.length/3;
	const pos=new Float32Array(parsed.positions);
	const normals=new Float32Array(N*3);
	const colors=new Float32Array(N*3);

	for (let i=0;i<N;i++)
		{
		const m=mats[parsed.vertMat[i]]||{Kd:[0.8,0.8,0.8]};
		colors[i*3]=m.Kd[0];
		colors[i*3+1]=m.Kd[1];
		colors[i*3+2]=m.Kd[2];
		}

	// Per-object color override — useful for OBJs that have no .mtl but
	// where we want to color each named group differently (e.g. IGSE
	// 'intergalactic' white, 'space_empire' orange).
	if (colorMap&&parsed.objects)
		{
		for (const o of parsed.objects)
			{
			const c=colorMap[o.name];
			if (!c) continue;
			for (let i=o.vStart;i<o.vEnd;i++)
				{
				colors[i*3]=c[0];
				colors[i*3+1]=c[1];
				colors[i*3+2]=c[2];
				}
			}
		}

	const fi=parsed.faceIdx;
	for (let f=0;f<fi.length;f+=3)
		{
		const ia=fi[f],ib=fi[f+1],ic=fi[f+2];
		const ax=pos[ia*3],ay=pos[ia*3+1],az=pos[ia*3+2];
		const bx=pos[ib*3],by=pos[ib*3+1],bz=pos[ib*3+2];
		const cx=pos[ic*3],cy=pos[ic*3+1],cz=pos[ic*3+2];
		const ex=bx-ax,ey=by-ay,ez=bz-az;
		const gx=cx-ax,gy=cy-ay,gz=cz-az;
		// Area-weighted cross product (unnormalized) accumulated at each vertex
		// gives a smooth normal that respects triangle area.
		const nx=ey*gz-ez*gy;
		const ny=ez*gx-ex*gz;
		const nz=ex*gy-ey*gx;
		normals[ia*3]+=nx; normals[ia*3+1]+=ny; normals[ia*3+2]+=nz;
		normals[ib*3]+=nx; normals[ib*3+1]+=ny; normals[ib*3+2]+=nz;
		normals[ic*3]+=nx; normals[ic*3+1]+=ny; normals[ic*3+2]+=nz;
		}
	for (let i=0;i<N;i++)
		{
		const x=normals[i*3],y=normals[i*3+1],z=normals[i*3+2];
		const L=Math.hypot(x,y,z)||1;
		normals[i*3]=x/L; normals[i*3+1]=y/L; normals[i*3+2]=z/L;
		}

	let minX=Infinity,maxX=-Infinity,minY=Infinity,maxY=-Infinity,minZ=Infinity,maxZ=-Infinity;
	for (let i=0;i<N;i++)
		{
		const x=pos[i*3],y=pos[i*3+1],z=pos[i*3+2];
		if (x<minX) minX=x; if (x>maxX) maxX=x;
		if (y<minY) minY=y; if (y>maxY) maxY=y;
		if (z<minZ) minZ=z; if (z>maxZ) maxZ=z;
		}

	const indices=N<65536?new Uint16Array(fi):new Uint32Array(fi);

	// Compute per-object bbox + home-center from the parsed objects metadata.
	const objects=(parsed.objects||[]).map(o=>
		{
		let oxMin=Infinity,oxMax=-Infinity,oyMin=Infinity,oyMax=-Infinity,ozMin=Infinity,ozMax=-Infinity;
		for (let i=o.vStart;i<o.vEnd;i++)
			{
			const x=pos[i*3],y=pos[i*3+1],z=pos[i*3+2];
			if (x<oxMin) oxMin=x; if (x>oxMax) oxMax=x;
			if (y<oyMin) oyMin=y; if (y>oyMax) oyMax=y;
			if (z<ozMin) ozMin=z; if (z>ozMax) ozMax=z;
			}
		return {
			name:o.name,
			indexStart:o.indexStart,
			indexCount:o.indexCount,
			home:[0.5*(oxMin+oxMax),0.5*(oyMin+oyMax),0.5*(ozMin+ozMax)],
			halfW:0.5*(oxMax-oxMin),							// used by the left-entry fly-in to park each unit fully off-stage
			};
		});

	return {positions:pos,normals,colors,indices,count:indices.length,N,bbox:{min:[minX,minY,minZ],max:[maxX,maxY,maxZ]},objects};
	}

// Far clip plane. Models must be authored small enough that the auto-fit camera
// distance (see `dist` in render()) stays inside this -- a mesh whose bbox is
// large relative to its on-screen placeholder pushes the camera back a long way.
// Raising this is NOT the fix for a vanished model: it costs depth precision for
// every caller. Scale the model instead (svg_to_obj.py --scale).
const Z_FAR=20000;

/* ---------- Per-letter animation units ---------- */

// Each unit is a set of OBJ objects that move together during the fly-in.
// O1 carries its eye+brow+pupil+highlight; same for O2; the TM rides with I.
// Order here is left-to-right — that's also the release order during the
// rubber-band intro.
// Each unit: {name, members}. `members` is an array of OBJ object names
// that move together. If a logo's OBJ has unique names per object, members
// is a list of names. For OBJs with duplicate names (e.g., the IGSE
// wordmark where 24 letters share two names), pass opts.units='auto' to
// InitLogo3D and each OBJ object becomes its own unit by index.
export const KANOOGI_UNITS=[
	{name:'K',  members:['K']},
	{name:'A',  members:['A']},
	{name:'N',  members:['N']},
	{name:'O1', members:['O1','Eye_L','Brow_L','Pupil_L','Highlight_L']},
	{name:'O2', members:['O2','Eye_R','Brow_R','Pupil_R','Highlight_R']},
	{name:'G',  members:['G']},
	{name:'I',  members:['I','TM']},
];

const FLY_DURATION=0.9;												// seconds per-letter for full snap+settle
const FLY_STAGGER_SPAN=0.6;											// total spread time over which all units release
const FLY_FADE_DUR=0.5;

// Standard easeOutElastic — rubber-band-style snap with decaying overshoot.
// u=0 → 0, u=1 → 1. Briefly exceeds 1 around u≈0.1 then settles.
// Signed shortest angular distance from a to b, in (-pi, pi]. Rotations are kept
// mod 2*pi, so a naive b-a can be up to 2*pi and would send the logo the long way
// round on the settle-back -- e.g. resting at 5.9rad with home at 0 should travel
// -0.38rad, not +5.9.
function ShortestAngleDelta(a,b)
	{
	let d=(b-a)%(2*Math.PI);
	if (d>Math.PI) d-=2*Math.PI;
	if (d<-Math.PI) d+=2*Math.PI;
	return(d);
	}

// Decelerate into the target and STOP there. easeOutElastic rings past 1, which
// is the whole point for the logo and fatal for a row of words -- see the
// factor comment in render().
function easeOutCubic(u)
	{
	if (u<=0) return 0;
	if (u>=1) return 1;
	const p=1-u;
	return 1-p*p*p;
	}

function easeOutElastic(u)
	{
	if (u<=0) return 0;
	if (u>=1) return 1;
	const c4=(2*Math.PI)/3;
	return Math.pow(2,-10*u)*Math.sin((u*10-0.75)*c4)+1;
	}

// Generate per-unit off-screen start deltas. `baseMag` is the inner
// magnitude — typically a multiple of the current view-frustum extent so
// letters always start fully off-screen regardless of viewport aspect or
// logo size.
function genFlyinOffsets(N,baseMag)
	{
	const out=[];
	for (let i=0;i<N;i++)
		{
		// Default (the logo): distribute base angles around the circle then
		// jitter each so they don't all march from one quadrant.
		const baseAngle=((i+0.5)/N)*2*Math.PI;
		const angle=baseAngle+(Math.random()-0.5)*Math.PI;
		const mag=baseMag*(1.0+Math.random()*0.4);
		out.push([Math.cos(angle)*mag,Math.sin(angle)*mag,0]);
		}
	return out;
	}

// Resolve a `opts.units` config (array of {name, members:[names]}) into an
// internal form (array of {name, indices:[number]}). For OBJ object names
// that appear multiple times in the OBJ, ALL matching indices are bundled
// into the unit. If `units` is 'auto' or undefined, every OBJ object becomes
// its own unit (by index) — handles OBJs with duplicate names cleanly.
// Per-unit start deltas for an edge entry (flyFrom 'left' or 'right').
// Deliberately NOT a shared magnitude: one magnitude for every unit is measured
// from each unit's OWN home, so on a wide mesh the units furthest from center
// start well inside the frustum and pop into existence mid-sentence instead of
// entering from off-stage. This parks every unit's leading edge on the same
// off-screen line -- 1.06 * viewHalfW beyond the frustum -- whatever its width
// or resting position.
function genEdgeEntryOffsets(units,mesh,viewHalfW,from)
	{
	const edge=1.06*viewHalfW;
	const out=[];
	for (let i=0;i<units.length;i++)
		{
		let cx=0,hw=0,n=0;
		const idxs=(units[i]&&units[i].indices)||[];
		for (let k=0;k<idxs.length;k++)
			{
			const o=mesh.objects[idxs[k]];
			if (!o) continue;
			cx+=o.home[0];
			if (o.halfW>hw) hw=o.halfW;
			n++;
			}
		if (n) cx/=n;
		// left:  park the unit's RIGHT edge at -edge   ->  off = -edge - cx - hw
		// right: park the unit's LEFT  edge at +edge   ->  off = +edge - cx + hw
		out.push([(from==='right')?(edge-cx+hw):(-edge-cx-hw),0,0]);
		}
	return out;
	}

function resolveUnits(meshObjects,units)
	{
	if (units==='auto'||!units)
		return meshObjects.map((o,i)=>({name:`${o.name}_${i}`,indices:[i]}));
	const out=[];
	for (const u of units)
		{
		const indices=[];
		for (const memberName of (u.members||[]))
			{
			for (let i=0;i<meshObjects.length;i++)
				if (meshObjects[i].name===memberName) indices.push(i);
			}
		if (u.indices) for (const idx of u.indices) indices.push(idx);
		out.push({name:u.name,indices});
		}
	return out;
	}

/* ---------- Renderer ---------- */

function Logo3DRenderer(canvas,backend,mesh,targetEl,units)
	{
	this.canvas=canvas;
	this.backend=backend;											// KAN_Logo3DGPU.js: the WebGPU or the WebGL tier
	this.tier=backend.tier;
	this.mesh=mesh;
	this.targetEl=targetEl;											// element whose rect we project the logo onto each frame
	this.units=units||[];											// resolved [{name, indices:[number]}, ...]
	// Live-tweakable lighting params, one copy of LOGO_LIGHTING_DEFAULTS (see
	// KAN_Logo3DGPU.js for what each defaults to and why). Values are user-tuned
	// via the ?debug=light HUD; mutate `this.lighting` to change at runtime.
	this.lighting=NewLighting();
	this.frameValues={};											// FrameValues() scratch, reused every frame
	// Manual orientation offset (radians) — applied around the mesh
	// center on top of the spin/wobble. The tuner drives these.
	// Per-page rotation is set via opts.rotation in InitLogo3D.
	this.rotation={x:0,y:0,z:0};
	// While true, suppress the idle Y-wobble — used by the tuner to
	// freeze the logo while the user is dragging a rotation slider.
	this.pauseWobble=false;
	// Settle-back. Clicking mid-spin BANKS the partial rotation into this.rotation
	// (see the click handler), so rapid clicking leaves the logo at an arbitrary
	// pose that never recovers on its own. With settleAfterSec set, the logo eases
	// back to the pose the page opened with once the user stops clicking.
	this.homeRotation=null;											// snapshot of the opening pose; null = feature off
	this.settleAfterSec=0;											// idle seconds before settling starts; 0 = never
	this.lastClickT=0;												// performance.now() of the most recent click; 0 = nothing to settle
	this.settleFrom=null;											// pose when the settle began
	this.settleStartT=null;											// performance.now() when the settle began
	// Optional per-page lighting animator: fn(t, lighting) mutated each
	// frame. Set via opts.lightingAnimate in InitLogo3D. While
	// pauseLightingAnim is true (e.g. user is dragging a tuner slider)
	// the animator is skipped so manual values stick.
	this.lightingAnimate=null;
	this.pauseLightingAnim=false;
	this.proj=mat4.create();
	this.modelView=mat4.create();
	this.normalMatrix=new Float32Array(9);
	this.introStartT=null;											// performance.now() at first visible frame; RESET on click so intro plays again
	this.pageVisibleT=null;											// performance.now() at first visible frame; LATCHED ONCE — does not reset on click
	// Render-loop warmup tracking. We refuse to latch introStartT until
	// rAF has fired a few times at a normal cadence — see render() for
	// the rationale (cold-boot ticks can be hundreds of ms apart, which
	// would let the intro burn through its timeline before the modal is
	// actually painted).
	this.lastWarmupFrame=null;
	this.stableFrameCount=0;
	this.baseMode='flyin';											// 'flyin' (default, with rubber-band intro) or 'static' (no intro, boots at idle).  Honored only for the initial page-load animation; clicks no longer toggle to it.
	this.introMode='flyin';											// current intro mode
	this.flyinOffsets=null;											// generated when fly-in mode activates (per-unit start delta)

	// Fly-in shaping. All three are OPT-IN via InitLogo3D opts; leaving them null
	// reproduces the logo's original behaviour exactly (offsets scattered around
	// the circle, FLY_STAGGER_SPAN total spread, FLY_DURATION per unit).
	//
	// They exist for the home tagline, which is the same renderer driving a very
	// different shape: 8 words that must arrive ONE AT A TIME from the left, each
	// landing and settling before the next releases. The logo's defaults cannot
	// express that -- FLY_STAGGER_SPAN is a TOTAL spread, so the more units you
	// have the tighter they bunch, and at 8 units they overlap 4-5 deep.
	this.flyFrom=null;												// null = scatter (logo). 'left'/'right' = every unit enters from that screen edge.
	this.flyStaggerSec=null;										// null = FLY_STAGGER_SPAN/(N-1). A number = seconds BETWEEN releases, so it does not compress as N grows.
	this.flyDurationSec=null;										// null = FLY_DURATION.
	this.flyReverse=false;											// false = release in unit order (logo). true = last unit first, so a left entry never crosses what's already placed.
	this.flyNoOvershoot=false;										// false = easeOutElastic (logo, rings past the target). true = easeOutCubic, stops dead on contact.
	this.flyKnockMag=0;												// 0 = off. >0 = an arriving unit strikes the standing row, which recoils and springs back.
	this.flyKnockDurSec=0;											// 0 = the 0.15s default. Set it when the caller's cadence is tight: the knock MUST finish before the next unit releases.
	this.spinAxis=null;												// null until first click; clicks cycle through 'x'->'y'->'z'->'x'... and route the spin animation to the chosen axis.  null + introMode!=='spin' just means "no spin in progress yet".

	// SampleSpinAngle(t) - The shared spin curve used by both render() and the
	// click handler.  Two phases:
	//   0 .. T_SPIN     constant-velocity wind: N_REV revolutions in T_SPIN sec
	//   T_SPIN..+T_BOUNCE  damped sine bounce around the resting angle
	// After T_SPIN+T_BOUNCE it sits at SPIN_FINAL (=N_REV*2*pi, which mod 2*pi
	// is zero, so the logo returns to its starting orientation on that axis).
	// Factored out so the click handler can bank an in-flight spin into the
	// settled rotation when the user clicks a new axis mid-animation, avoiding
	// a visible snap back to rest.
	this.SampleSpinAngle=function(t)
		{
		const T_SPIN=0.9;
		const T_BOUNCE=0.6;
		const N_REV=2;
		const BOUNCE_AMP=0.20;
		const BOUNCE_TAU=0.15;
		const BOUNCE_OMEGA=2*Math.PI*4;
		const SPIN_FINAL=N_REV*2*Math.PI;
		if (t<T_SPIN) return((t/T_SPIN)*SPIN_FINAL);
		if (t<T_SPIN+T_BOUNCE)
			{
			const t1=t-T_SPIN;
			return(SPIN_FINAL+BOUNCE_AMP*Math.sin(BOUNCE_OMEGA*t1)*Math.exp(-t1/BOUNCE_TAU));
			}
		return(SPIN_FINAL);
		};
	this.lastW=0;
	this.lastH=0;
	this.framesDrawn=0;												// frames handed to the backend; harnesses read it as "is it drawing?"

	// Per-unit draw ranges: the mesh.objects each unit draws, by position in
	// mesh.objects (handles OBJs with duplicate object names — units reference
	// by index, not name).
	const objects=mesh.objects||[];
	this.unitRanges=this.units.map(u=>u.indices.map(idx=>objects[idx]).filter(Boolean));
	}

Logo3DRenderer.prototype.resize=function()
	{
	// Fullscreen overlay canvas — CSS is locked to 100vw x 100vh elsewhere.
	// Backing store tracks viewport size × devicePixelRatio for crisp
	// rendering across DPR changes.
	const dpr=window.devicePixelRatio||1;
	const cw=window.innerWidth;
	const ch=window.innerHeight;
	if (!cw||!ch) return;
	let W=Math.max(1,Math.round(cw*dpr));
	let H=Math.max(1,Math.round(ch*dpr));
	// WebGPU refuses a canvas texture past maxTextureDimension2D (8192 by
	// default; a 5K display at 2x is 10240 wide). Shrink both sides by the
	// same factor so the aspect, and so the projection, is unchanged; the
	// canvas is CSS-sized, so it just upscales a little.
	const maxDim=this.backend.maxDim||0;
	if (maxDim&&(W>maxDim||H>maxDim))
		{
		const k=maxDim/Math.max(W,H);
		W=Math.max(1,Math.floor(W*k));
		H=Math.max(1,Math.floor(H*k));
		}
	if (W===this.lastW&&H===this.lastH) return;
	this.canvas.width=W;
	this.canvas.height=H;
	this.lastW=W;
	this.lastH=H;
	this.backend.resize(W,H);
	// GL clip-space z. The WebGPU tier remaps z in its vertex shader, so both
	// tiers share this one matrix (KAN_Logo3DGPU.js, LOGO_WGSL).
	mat4.perspective(this.proj,12*Math.PI/180,W/H,10,Z_FAR);
	};

Logo3DRenderer.prototype.render=function()
	{
	this.resize();

	// Don't start the intro until the placeholder element (the original
	// <img>) is laid out and visible — that's the cue that the home page
	// has revealed its content. We project the mesh onto that element's
	// rect every frame, so its visibility doubles as our "ready" gate.
	const targetRect=this.targetEl?this.targetEl.getBoundingClientRect():null;
	const targetReady=targetRect&&targetRect.width>0&&targetRect.height>0
		&&window.getComputedStyle(this.targetEl.parentElement||this.targetEl).visibility==='visible';
	const N=this.units.length||1;
	// Default: FLY_STAGGER_SPAN is a TOTAL spread, so units bunch tighter as N
	// grows. Fine for the 7-unit logo; wrong for the 8-word tagline, where it
	// would put 4-5 words in the air at once. flyStaggerSec overrides with a
	// fixed gap BETWEEN releases, which does not compress with N.
	const flyDur=this.flyDurationSec||FLY_DURATION;
	const flyStagger=this.flyStaggerSec!==null&&this.flyStaggerSec!==undefined
		?this.flyStaggerSec
		:FLY_STAGGER_SPAN/Math.max(1,N-1);
	const flyEnd=(N-1)*flyStagger+flyDur;
	const KNOCK_IMPACT_FRAC=0.82;									// where in the arriving unit's flight it "lands" on its neighbour
	const KNOCK_DUR=this.flyKnockDurSec||0.15;							// seconds for the struck row to recoil and settle. It MUST finish before the next unit releases, or the row is never still when the next one lands and the collisions stop reading. Callers with a tight cadence pass flyKnockDur (the home tagline runs 0.17s flight / 0.261s gap, so 0.15 would overrun and it passes 0.11).
	const T_ROLL=3.0;												// rollin intro duration (seconds)
	const ROLL_SETTLE_DELAY=10.0;									// seconds after rollin ends before idle motion fades in

	if (this.introStartT===null)
		{
		if (!targetReady) return;
		// Render-loop stability gate. On a cold boot the first few rAF
		// ticks can fire hundreds of ms apart while the page is still
		// parsing modules and fetching assets — latching introStartT on
		// the very first eligible frame lets the intro animation (and
		// the wobble's t-baseline) burn through their timelines before
		// the browser has actually painted the modal. Require three
		// consecutive frames at a normal cadence (≤ ~32 ms apart, i.e.
		// ≥ 30 fps) before latching. Adds ~100 ms of warmup on a fast
		// boot; saves seconds of "started without me" on a slow one.
		const now=performance.now();
		if (this.lastWarmupFrame!==null)
			{
			const delta=now-this.lastWarmupFrame;
			if (delta>0&&delta<=32) this.stableFrameCount++;
			else this.stableFrameCount=0;
			}
		this.lastWarmupFrame=now;
		if (this.stableFrameCount<3) return;
		this.introStartT=now;
		// pageVisibleT is latched ONCE on first visibility — survives
		// click-triggered intro restarts so the lighting animator (which
		// is meant as a once-on-page-load effect) keeps its own monotonic
		// clock instead of replaying from t=0 each click.
		if (this.pageVisibleT===null) this.pageVisibleT=this.introStartT;
		}

	const t=(performance.now()-this.introStartT)/1000;

	// Settle-back: after settleAfterSec of no clicking, ease the banked rotation
	// home. Runs on this.rotation (the RESTING pose), not on the spin term, so it
	// composes with an in-flight spin rather than fighting it -- and a completed
	// spin contributes a whole number of revolutions, so there is nothing of the
	// spin left to undo.
	if (this.homeRotation&&this.settleAfterSec>0&&this.lastClickT)
		{
		const SETTLE_DUR=0.9;										// seconds to travel home once it starts
		const nowMs=performance.now();
		if ((nowMs-this.lastClickT)/1000>=this.settleAfterSec)
			{
			if (this.settleStartT===null)
				{
				const off=['x','y','z'].some(a=>Math.abs(ShortestAngleDelta(this.rotation[a],this.homeRotation[a]))>1e-4);
				if (off) { this.settleFrom={x:this.rotation.x,y:this.rotation.y,z:this.rotation.z}; this.settleStartT=nowMs; }
				else this.lastClickT=0;								// already home: stand down until the next click
				}
			if (this.settleStartT!==null)
				{
				const u=Math.max(0,Math.min(1,(nowMs-this.settleStartT)/1000/SETTLE_DUR));
				const e=easeOutCubic(u);
				for (const a of ['x','y','z'])
					this.rotation[a]=this.settleFrom[a]+ShortestAngleDelta(this.settleFrom[a],this.homeRotation[a])*e;
				if (u>=1)
					{
					this.rotation.x=this.homeRotation.x;			// land exactly on it, no float drift
					this.rotation.y=this.homeRotation.y;
					this.rotation.z=this.homeRotation.z;
					this.settleStartT=null;
					this.lastClickT=0;
					}
				}
			}
		}

	// ---- Mode-dependent group transform + idle fade ---------------------
	const FADE_DUR=0.5;
	let groupRotX=0;
	let groupRotY=0;
	let groupRotZ=0;
	let idleFade=0;

	if (this.introMode==='spin')
		{
		// Spin: 2 turns of constant-velocity rotation, then damped-sine bounce
		// around the resting angle, then idle fades in.  Curve formula lives
		// in this.SampleSpinAngle so the click handler can bank an in-flight
		// spin angle into the settled rotation when the user changes axes.
		const T_SPIN=0.9;
		const T_BOUNCE=0.6;
		const groupRot=this.SampleSpinAngle(t);
		if (t>=T_SPIN+T_BOUNCE)
			idleFade=Math.min(1,(t-T_SPIN-T_BOUNCE)/FADE_DUR);

		// Route the spin to the chosen axis.  spinAxis is set by the click
		// handler and cycles 'x'->'y'->'z'.  Defaults to 'y' when null so
		// any caller that triggers introMode='spin' without setting an axis
		// (back-compat with the previous behavior) still spins on Y.
		const axis=this.spinAxis||'y';
		if (axis==='x') groupRotX=groupRot;
		else if (axis==='z') groupRotZ=groupRot;
		else groupRotY=groupRot;
		}
	else if (this.introMode==='flyin')
		{
		idleFade=t<flyEnd?0:Math.min(1,(t-flyEnd)/FADE_DUR);
		}
	else if (this.introMode==='rollin')
		{
		// rollin: extra X-rotation animates from -90° down to the settled
		// rotation.x with a quintic ease-out, the logo sits motionless for
		// ROLL_SETTLE_DELAY seconds, THEN the idle motion fades in. Gives
		// it a "land softly and rest before breathing" feel.
		const idleStart=T_ROLL+ROLL_SETTLE_DELAY;
		idleFade=t<idleStart?0:Math.min(1,(t-idleStart)/FADE_DUR);
		}
	else // 'static' — no intro, just idle wobble from t=0
		{
		idleFade=1;
		}

	// Idle motion: gentle Y-rock + small X-rock at different periods so
	// they don't sync up. Sin args use tIdle (time since idle motion
	// began) so the wobbles start at 0 amplitude — without this, when
	// idleFade ramps up over FADE_DUR the wobble value snaps to whatever
	// sin(t*...) happens to be at that moment, producing the visible
	// "rush" the user reported after the 10s settle.
	let idleStartT;
	if (this.introMode==='spin') idleStartT=0.9+0.6;					// T_SPIN + T_BOUNCE
	else if (this.introMode==='flyin') idleStartT=flyEnd;
	else if (this.introMode==='rollin') idleStartT=T_ROLL+ROLL_SETTLE_DELAY;
	else idleStartT=0;												// 'static'
	const tIdle=Math.max(0,t-idleStartT);
	const wobbleY=this.pauseWobble?0:Math.sin(tIdle*0.45)*0.10*idleFade;	// ~5.7° Y rock
	const wobbleX=this.pauseWobble?0:Math.sin(tIdle*0.31)*0.087*idleFade;	// ~5° X rock, different period drifts it out of sync with Y

	// rollin intro: animate an extra X-rotation from -90° + settled-X back
	// to settled-X over T_ROLL seconds with a quintic ease-out — softer
	// arrival than cubic so it doesn't hit the resting angle abruptly.
	let rollX=0;
	if (this.introMode==='rollin'&&t<T_ROLL)
		{
		const u=t/T_ROLL;
		const eased=1-Math.pow(1-u,5);								// ease-out quintic
		const startOffset=-Math.PI/2-this.rotation.x;
		rollX=startOffset*(1-eased);
		}

	// ---- Camera + frustum -----------------------------------------------
	// Fullscreen canvas: compute distance + view-space shift to project the
	// mesh onto the placeholder rect (the original <img> position). Mesh
	// keeps the same on-screen size + position it had before the canvas
	// went fullscreen; the rest of the canvas just gives the fly-in room
	// to enter from the actual screen edges.
	const bbox=this.mesh.bbox;
	const cx=0.5*(bbox.min[0]+bbox.max[0]);
	const cy=0.5*(bbox.min[1]+bbox.max[1]);
	const cz=0.5*(bbox.min[2]+bbox.max[2]);
	const halfW=0.5*(bbox.max[0]-bbox.min[0]);
	const halfH=0.5*(bbox.max[1]-bbox.min[1]);
	const fovHalf=6*Math.PI/180;									// 12° FOV -> half = 6°
	const cssW=window.innerWidth;
	const cssH=window.innerHeight;
	const aspect=cssW/cssH||1;
	// Compute distance so the rendered mesh's on-screen height matches the
	// placeholder height. (In the OLD setup, the canvas was 20% taller
	// than the placeholder and the mesh filled 70.6% of canvas — the net
	// effect was mesh ≈ placeholder height. Matching the placeholder
	// directly preserves that on-screen size, simpler.)
	const targetH=targetRect?targetRect.height:halfH*2;
	const dist=halfH*cssH/(targetH*Math.tan(fovHalf));
	// A model that is big relative to its placeholder pushes the camera past
	// Z_FAR and is clipped away ENTIRELY -- blank screen, no GL error, nothing
	// in the console. Say so once rather than leaving the next caller to guess.
	if (dist>=Z_FAR&&!this.warnedFar)
		{
		this.warnedFar=true;
		console.warn(`[Logo3D] model too large for the frustum: fit distance ${Math.round(dist)} exceeds the ${Z_FAR} far plane, so nothing will draw. Scale the model down (svg_to_obj.py --scale); mesh bbox height is ${(halfH*2).toFixed(0)} against a ${targetH.toFixed(0)}px placeholder.`);
		}
	// View-space offset that lands the mesh center at the placeholder's
	// screen center. Convert pixel position → NDC → view space.
	const targetCxPx=targetRect?targetRect.left+0.5*targetRect.width:0.5*cssW;
	const targetCyPx=targetRect?targetRect.top+0.5*targetRect.height:0.5*cssH;
	const ndcX=(targetCxPx/cssW)*2-1;
	const ndcY=-((targetCyPx/cssH)*2-1);
	const viewHalfH=dist*Math.tan(fovHalf);
	const viewHalfW=viewHalfH*aspect;
	const viewOffX=ndcX*viewHalfW;
	const viewOffY=ndcY*viewHalfH;

	// Lazy-generate fly-in offsets once the camera is set up. Magnitude is
	// scaled to the current view extent so letters start fully off-screen
	// regardless of viewport aspect or how big the logo's mesh is. The 2.3x
	// inner radius matches the Kanoogi-era pullback (which the user
	// confirmed felt right) on a 16:10 viewport.
	if (this.introMode==='flyin'&&!this.flyinOffsets)
		{
		const baseMag=Math.max(viewHalfH,viewHalfW)*2.3;
		this.flyinOffsets=(this.flyFrom==='left'||this.flyFrom==='right')
			?genEdgeEntryOffsets(this.units,this.mesh,viewHalfW,this.flyFrom)
			:genFlyinOffsets(N,baseMag);
		}

	const L=this.lighting;
	// Expose mesh bbox to the per-page animator so it can position the
	// optional point light in object-space units relative to the logo
	// itself ("a half-width to the right", "just in front of the front
	// face") rather than guessing camera distance. Cheap one-time set
	// each frame; ignored by animators that don't use it.
	L.meshBbox=this.mesh.bbox;
	if (this.lightingAnimate&&!this.pauseLightingAnim)
		{
		// Page-load clock — keeps growing even when clicks reset
		// introStartT, so the lighting "intro" is once-only.
		const tPage=(performance.now()-this.pageVisibleT)/1000;
		this.lightingAnimate(tPage,L);
		}

	// Per-frame uniforms, from ONE function for both tiers (KAN_Logo3DGPU.js),
	// so a page's lighting preset cannot reach one tier and not the other.
	const backend=this.backend;
	backend.begin(FrameValues(L,this.proj,this.frameValues));

	// ---- Per-unit draw -------------------------------------------------
	// Each unit (letter + its features) gets its own modelView so the
	// fly-in can offset units independently. In spin mode all units share
	// offset=0 and the group rotation animates them together.
	const mv=this.modelView;
	const nm=this.normalMatrix;
	for (let ui=0;ui<this.units.length;ui++)
		{
		let ox=0,oy=0,oz=0;
		if (this.introMode==='flyin'&&this.flyinOffsets)
			{
			const startOff=this.flyinOffsets[ui];
			// Arrival order. 'reverse' releases the LAST unit first, so every
			// arriving unit's home lies to the LEFT of everything already
			// standing and its path never crosses any of it. That is the only
			// way an entry from the left can assemble a left-to-right layout
			// without passing through it: in unit order, each arrival has to
			// cross every word already placed to reach a home on their right.
			const order=this.flyReverse?(N-1-ui):ui;
			const releaseT=order*flyStagger;
			const localT=t-releaseT;
			const u=Math.max(0,Math.min(1,localT/flyDur));
			// easeOutElastic RINGS PAST 1 -- peak factor -0.373, i.e. the unit
			// travels 37% of its flight distance BEYOND its home before settling
			// back. The logo wants that (its units converge on a shared center
			// with nothing beside them); a row of words cannot have it, because
			// for the tagline 37% is ~3.6 word-widths straight through the
			// neighbour. flyNoOvershoot decelerates into contact and stops dead
			// there, which is also the correct cradle behaviour -- the incoming
			// ball keeps none of its momentum, all of it goes into the row.
			const factor=this.flyNoOvershoot?(1-easeOutCubic(u)):(1-easeOutElastic(u));
			ox=startOff[0]*factor;
			oy=startOff[1]*factor;
			oz=startOff[2]*factor;

			// Cradle jolt. Every LATER arrival strikes the standing row, and the
			// whole row takes the SAME displacement -- uniform, so relative
			// spacing is untouched and no two units can overlap each other. It
			// settles before the next release (see KNOCK_DUR). Half-sine * linear
			// decay: zero at both ends, so it neither pops on nor leaves a
			// residual offset. The push follows the side the striker arrives
			// from: entering from the left it shoves the row +X, entering from
			// the right it shoves it -X.
			if (this.flyKnockMag>0)
				{
				const knockDir=(this.flyFrom==='right')?-1:1;
				for (let m=order+1;m<N;m++)
					{
					const impactT=m*flyStagger+flyDur*KNOCK_IMPACT_FRAC;
					const k=(t-impactT)/KNOCK_DUR;
					if (k<0) break;								// this strike and every later one is still in the future
					if (k<1)
						{
						ox+=knockDir*this.flyKnockMag*Math.sin(Math.PI*k)*(1-k);
						break;
						}
					}
				}
			}

		mat4.identity(mv);
		mat4.translate(mv,mv,[viewOffX,viewOffY,-dist]);				// land mesh center at placeholder screen center
		// Manual orientation tuning is applied around the mesh center along
		// with the idle Y-wobble + spin.  Order (vertex view): RZ, RY, RX.
		// groupRotX/Y/Z carry the click-driven spin animation; exactly one is
		// non-zero at a time, picked by this.spinAxis.
		mat4.rotateX(mv,mv,this.rotation.x+rollX+wobbleX+groupRotX);
		mat4.rotateY(mv,mv,this.rotation.y+groupRotY+wobbleY);
		mat4.rotateZ(mv,mv,this.rotation.z+groupRotZ);
		mat4.translate(mv,mv,[-cx+ox,-cy+oy,-cz+oz]);

		// Normal matrix from upper-left 3x3 of modelView (transpose of
		// inverse — for our rotations + translations this is just the
		// rotation 3x3).
		const m=mv;
		const a00=m[0],a01=m[1],a02=m[2];
		const a10=m[4],a11=m[5],a12=m[6];
		const a20=m[8],a21=m[9],a22=m[10];
		const b00=a11*a22-a12*a21;
		const b01=a12*a20-a10*a22;
		const b02=a10*a21-a11*a20;
		const b10=a02*a21-a01*a22;
		const b11=a00*a22-a02*a20;
		const b12=a01*a20-a00*a21;
		const b20=a01*a12-a02*a11;
		const b21=a02*a10-a00*a12;
		const b22=a00*a11-a01*a10;
		const det=a00*b00+a01*b01+a02*b02;
		const invDet=det!==0?1/det:0;
		nm[0]=b00*invDet; nm[1]=b10*invDet; nm[2]=b20*invDet;
		nm[3]=b01*invDet; nm[4]=b11*invDet; nm[5]=b21*invDet;
		nm[6]=b02*invDet; nm[7]=b12*invDet; nm[8]=b22*invDet;

		backend.unit(ui,mv,nm,this.unitRanges[ui]);
		}
	backend.end();
	this.framesDrawn++;
	};

/* ---------- In-page lighting tuner ---------- */
// Mounted by InitLogo3D when the URL has ?debug=light (or ?debug
// containing 'light'). Fixed-position panel of sliders that bind to the
// renderer's `lighting` object in real time, plus a textarea snippet you
// can copy back into the FSHAD_SRC defaults once you've dialed in values.

function MountLightingTuner(renderer)
	{
	const L=renderer.lighting;
	const R=renderer.rotation;
	const RAD=Math.PI/180;
	// Each slider declaration:
	//   target — 'lighting' or 'rotation'
	//   key    — field name on the target object
	//   degrees — if true, slider shows degrees; we convert to radians for the field
	const sliders=[
		{target:'lighting',key:'lx',          label:'Light X',   min:-1,  max:1,   step:0.02, fmt:v=>v.toFixed(2)},
		{target:'lighting',key:'ly',          label:'Light Y',   min:-1,  max:1,   step:0.02, fmt:v=>v.toFixed(2)},
		{target:'lighting',key:'lz',          label:'Light Z',   min:-1,  max:1,   step:0.02, fmt:v=>v.toFixed(2)},
		{target:'lighting',key:'ambient',     label:'Ambient',   min: 0,  max:1,   step:0.01, fmt:v=>v.toFixed(2)},
		{target:'lighting',key:'specStrength',label:'Spec Str',  min: 0,  max:2,   step:0.01, fmt:v=>v.toFixed(2)},
		{target:'lighting',key:'shininess',   label:'Shininess', min: 1,  max:128, step:1   , fmt:v=>v.toFixed(0)},
		{target:'rotation',key:'x',           label:'Rot X°',    min:-180,max:180, step:1, degrees:true, fmt:v=>v.toFixed(0)+'°'},
		{target:'rotation',key:'y',           label:'Rot Y°',    min:-180,max:180, step:1, degrees:true, fmt:v=>v.toFixed(0)+'°'},
		{target:'rotation',key:'z',           label:'Rot Z°',    min:-180,max:180, step:1, degrees:true, fmt:v=>v.toFixed(0)+'°'},
	];

	const root=document.createElement('div');
	root.id='logo3d-tuner';
	root.style.cssText=[
		'position:fixed','bottom:16px','right:16px','z-index:9999',	// bottom-right keeps it clear of top-row logos
		'background:rgba(0,0,0,0.85)','border:1px solid #555','border-radius:6px',
		'padding:10px 12px','color:#ddd','font:11px/1.4 ui-monospace,monospace',
		'min-width:240px','pointer-events:auto','user-select:none',
		].join(';');

	const title=document.createElement('div');
	title.textContent='Logo3D Lighting';
	title.style.cssText='border-bottom:1px solid #555;padding-bottom:4px;margin-bottom:6px;font-weight:bold;';
	root.appendChild(title);

	let lastGroup=null;
	for (const s of sliders)
		{
		// Insert a thin separator between sections (lighting vs rotation).
		if (s.target!==lastGroup&&lastGroup!==null)
			{
			const sep=document.createElement('div');
			sep.style.cssText='height:1px;background:#444;margin:6px 0;';
			root.appendChild(sep);
			}
		lastGroup=s.target;

		const obj=renderer[s.target];
		const initialDisplay=s.degrees?obj[s.key]/RAD:obj[s.key];

		const wrap=document.createElement('div');
		wrap.style.cssText='display:grid;grid-template-columns:70px 1fr 46px;gap:4px;align-items:center;margin:3px 0;';
		const lbl=document.createElement('span');
		lbl.textContent=s.label;
		const inp=document.createElement('input');
		inp.type='range';
		inp.min=s.min; inp.max=s.max; inp.step=s.step;
		inp.value=initialDisplay;
		inp.style.cssText='width:100%;cursor:ew-resize;';
		const val=document.createElement('span');
		val.textContent=s.fmt(initialDisplay);
		val.style.cssText='text-align:right;color:#9cf;';
		inp.addEventListener('input',()=>
			{
			const v=parseFloat(inp.value);
			obj[s.key]=s.degrees?v*RAD:v;
			val.textContent=s.fmt(v);
			updateSnippet();
			});
		// While the user is dragging a tuner slider, freeze the motion
		// that the slider type affects so the edit is visible:
		//   rotation sliders → pause the idle Y/X wobble
		//   lighting sliders → pause the per-page lightingAnimate so the
		//                      user's manual value isn't overwritten next
		//                      frame
		// Released on a window-wide pointerup so the wobble/animation
		// resume even if the cursor strayed off the slider while dragging.
		inp.addEventListener('pointerdown',()=>
			{
			if (s.target==='rotation') renderer.pauseWobble=true;
			if (s.target==='lighting') renderer.pauseLightingAnim=true;
			});
		wrap.appendChild(lbl); wrap.appendChild(inp); wrap.appendChild(val);
		root.appendChild(wrap);
		}
	window.addEventListener('pointerup',()=>
		{
		renderer.pauseWobble=false;
		renderer.pauseLightingAnim=false;
		});

	const snippet=document.createElement('textarea');
	snippet.readOnly=true;
	snippet.rows=5;
	snippet.style.cssText='width:100%;margin-top:6px;background:#111;color:#cfc;border:1px solid #444;font:10px/1.3 ui-monospace,monospace;padding:4px;box-sizing:border-box;';
	root.appendChild(snippet);
	function updateSnippet()
		{
		snippet.value=
			`lx:${L.lx.toFixed(2)}, ly:${L.ly.toFixed(2)}, lz:${L.lz.toFixed(2)},\n`+
			`ambient:${L.ambient.toFixed(2)}, specStrength:${L.specStrength.toFixed(2)},\n`+
			`shininess:${L.shininess.toFixed(0)}\n`+
			`rotX:${(R.x/RAD).toFixed(0)}°, rotY:${(R.y/RAD).toFixed(0)}°, rotZ:${(R.z/RAD).toFixed(0)}°`;
		}
	updateSnippet();

	const hint=document.createElement('div');
	hint.style.cssText='margin-top:6px;color:#888;font-size:10px;';
	hint.textContent='?debug=light';
	root.appendChild(hint);

	document.body.appendChild(root);
	}

// ResolveMeshUrl() - honeypot mesh-relocation override (NodeLibs/KanHoneypot.js
// MeshRelocateScript()). A honeypot member's server-rendered HTML sets
// window.__kanMeshBase before this module runs; every other visitor never has
// it set, so this is a silent no-op for everyone but that one flagged account.
function ResolveMeshUrl(url)
	{
	try
		{
		const base=(typeof window!=='undefined')&&window.__kanMeshBase;
		return(base?String(url).replace(/^\/models\//,base.replace(/\/+$/,'')+'/'):url);
		}
	catch (e) { return(url); }
	}

/* ---------- Public init ---------- */

// LoadLogoMesh() - fetch + parse the OBJ (and its MTL), build the mesh, resolve the units.
// Shared by every tier, and started before the tier is settled so the fetch overlaps the
// WebGPU device request.
async function LoadLogoMesh(objUrl,opts)
	{
	const baseUrl=objUrl.replace(/\/[^\/]*$/,'/');
	const objText=await FetchText(objUrl);
	const parsed=ParseOBJ(objText);
	const mtlText=parsed.mtllib?await FetchText(baseUrl+parsed.mtllib):'';
	const mats=ParseMTL(mtlText);
	const mesh=BuildMesh(parsed,mats,opts.colorMap);
	// Resolve units now that we have the parsed mesh objects. Caller can
	// pass opts.units = explicit array of {name, members:[names]}, the
	// string 'auto' to make each OBJ object its own unit (handy for OBJs
	// with duplicate object names), or omit it for the default Kanoogi
	// home-page units.
	const units=resolveUnits(parsed.objects||[],opts.units===undefined?KANOOGI_UNITS:opts.units);
	return({mesh,units});
	}

// OverlayCanvas() - the fullscreen overlay canvas, appended to <body>. The original <img>
// stays in place as the layout placeholder (visibility-hidden so it reserves the same space
// the SVG used to). Each frame the renderer reads the <img>'s screen rect and projects the
// mesh there — so the logo lands at exactly the position the SVG would have, while the rest
// of the canvas gives the fly-in room to enter from the real screen edges instead of
// clipping to a small render box. A fresh one per tier attempt: a canvas that has handed
// out a 'webgpu' context can never give a 'webgl' one.
function OverlayCanvas(imgElement,opts)
	{
	const canvas=document.createElement('canvas');
	canvas.setAttribute('aria-hidden','true');
	// Inherit the placeholder's class — that lets per-page CSS (e.g.
	// .glow-image on the join-beta IGSE wordmark, which applies a
	// pulsing red drop-shadow) carry over to the canvas. Filter is
	// computed against the canvas's non-transparent pixels (the
	// rendered mesh), so the glow halos the letters.
	// NOTE: on an SVG element `className` is an SVGAnimatedString, not a
	// string -- assigning it straight across would stamp the canvas with
	// "[object SVGAnimatedString]". The home tagline passes its inline
	// <svg> as the placeholder, so read baseVal when that's what we got.
	const phClass=typeof imgElement.className==='string'
		?imgElement.className
		:(imgElement.className&&imgElement.className.baseVal)||'';
	if (phClass) canvas.className=phClass;
	// z-index defaults to 1 (above page content). Callers whose
	// placeholder lives inside a higher-stacking modal (e.g. the game
	// BEGIN modal whose backdrop is z-index:100) can pass opts.zIndex
	// to push the canvas above their stacking context so the rendered
	// logo isn't dimmed by the backdrop.
	const zIndex=typeof opts.zIndex==='number'?opts.zIndex:1;
	canvas.style.cssText=[
		'position:fixed',
		'top:0',
		'left:0',
		'width:100vw',
		'height:100vh',
		'pointer-events:none',
		'z-index:'+zIndex,
		].join(';');
	document.body.appendChild(canvas);
	return(canvas);
	}

// InitLogo3D(imgElement, opts) -> Promise<handle | false>. See the file header for the
// tiers. opts.webgpuOnly: no WebGL tier at all (not even its probe) - the <img> instead.
// opts.gpuTimeoutMs: the WebGPU deadline, default LOGO_GPU_TIMEOUT_MS (tests shorten it).
export async function InitLogo3D(imgElement,opts)
	{
	opts=opts||{};
	if (!imgElement) return false;
	// Reveal the bitmap fallback. Pages that opt into the 3D promotion
	// add the `kan-logo3d-target` class to their <img>, which the shared
	// CSS rule renders at opacity:0/visibility:hidden — so the bitmap
	// doesn't flash before the canvas overlays it. On any failure
	// path here we add `.show-fallback`, which the CSS overrides back to
	// opacity:1/visibility:visible with a fade-in transition. We also
	// clear any inline visibility/opacity that the success path may have
	// set, so the CSS class wins (inline styles otherwise outrank CSS).
	const revealFallback=()=>
		{
		imgElement.style.visibility='';
		imgElement.style.opacity='';
		imgElement.classList.add('show-fallback');
		};
	const webgpuOnly=opts.webgpuOnly===true;

	// Tier 1 first. Without navigator.gpu there is nothing to wait for, and the WebGL probe
	// runs before anything is fetched, exactly as it did before the WebGPU tier existed.
	const gpuP=HasWebGPU()?AcquireLogoGPU(typeof opts.gpuTimeoutMs==='number'?opts.gpuTimeoutMs:LOGO_GPU_TIMEOUT_MS):null;
	if (!gpuP&&(webgpuOnly||!ProbeWebGL()))
		{
		console.debug(webgpuOnly?"[Logo3D] WebGPU unavailable — keeping the bitmap.":"[Logo3D] WebGPU and WebGL unavailable — keeping SVG.");	// debug, not log: dev diagnostic, hidden at default DevTools log level so regular users don't see it
		revealFallback();
		return false;
		}

	const meshP=LoadLogoMesh(ResolveMeshUrl(opts.objUrl||'/models/kanoogi_logo.obj'),opts);
	meshP.catch(()=>{});											// awaited below; this only stops a rejection that lands while we wait on the GPU from reading as unhandled

	let gpu=gpuP?await gpuP:null;									// from here on, non-null = this renderer holds a ref on the page's device
	if (gpu&&!gpu.ok)
		{
		console.debug(`[Logo3D] WebGPU unavailable (${gpu.reason}: ${gpu.detail})${webgpuOnly?'':' — trying WebGL'}`);	// debug: dev diagnostic
		gpu=null;
		}
	let canvas=null,backend=null,gl=null;
	try
		{
		const {mesh,units}=await meshP;

		// ---- Tier 1: WebGPU ----
		if (gpu)
			{
			canvas=OverlayCanvas(imgElement,opts);
			try
				{
				const ctx=canvas.getContext('webgpu');
				if (!ctx) throw new Error("canvas.getContext('webgpu') returned null");
				backend=await CreateWebGPUBackend(ctx,gpu,mesh,units.length);
				}
			catch (err)
				{
				console.warn(`[Logo3D] WebGPU tier failed${webgpuOnly?'':', trying WebGL'}:`,err);
				canvas.remove();
				canvas=null;
				ReleaseLogoGPU(gpu.device);
				gpu=null;
				}
			}

		// ---- Tier 2: WebGL, only now that WebGPU has failed ----
		if (!backend)
			{
			if (webgpuOnly||(gpuP&&!ProbeWebGL()))					// no navigator.gpu: probed above, before the fetch
				{
				console.debug("[Logo3D] no 3D tier available — keeping the bitmap.");	// debug: dev diagnostic
				revealFallback();
				return false;
				}
			canvas=OverlayCanvas(imgElement,opts);
			// premultipliedAlpha:true, as the WebGPU tier's alphaMode:'premultiplied'. The
			// shader writes alpha 1.0, so its colour IS premultiplied, and the MSAA resolve
			// scales an edge pixel's colour and alpha by coverage together. Declared
			// unpremultiplied (as it was before the WebGPU tier), the compositor scaled that
			// colour by alpha a SECOND time: a dark fringe round every letter, and the main
			// pixel difference between the two tiers (pinned-frame diff, 2026-10-11).
			gl=canvas.getContext('webgl',{antialias:true,premultipliedAlpha:true,alpha:true})
				||canvas.getContext('experimental-webgl');
			if (!gl)
				{
				console.debug("[Logo3D] context lost between probe and create — keeping SVG");	// debug (not log): dev-only diagnostic
				canvas.remove();
				revealFallback();
				return false;
				}
			backend=CreateWebGLBackend(gl,mesh);
			}

		// Keep the SVG <img> in the page flow as our layout placeholder,
		// but make it invisible — we render in its place via the canvas.
		imgElement.style.visibility='hidden';

		const renderer=new Logo3DRenderer(canvas,backend,mesh,imgElement,units);

		// opts.intro:    animation played on first page load
		// opts.baseMode: animation that clicks toggle back to from 'spin'.
		//                Defaults to opts.intro when not specified — so a
		//                page that wants the load animation to be the same
		//                as its click-cycle animation only needs to set intro.
		// Each can independently be: 'flyin' | 'static' | 'rollin'.
		const VALID_MODES=['flyin','static','rollin'];
		const introMode=VALID_MODES.indexOf(opts.intro)>=0?opts.intro:'flyin';
		const baseMode=VALID_MODES.indexOf(opts.baseMode)>=0?opts.baseMode:introMode;
		renderer.introMode=introMode;
		renderer.baseMode=baseMode;

		// opts.flyFrom / opts.flyStagger / opts.flyDuration: shape the fly-in.
		// All optional; omitting them leaves the logo's original behaviour
		// untouched. See the property declarations on the renderer for why the
		// tagline needs them (8 words, one at a time, all from the left).
		if (opts.flyFrom==='left'||opts.flyFrom==='right') renderer.flyFrom=opts.flyFrom;
		if (typeof opts.flyStagger==='number') renderer.flyStaggerSec=opts.flyStagger;
		if (typeof opts.flyDuration==='number') renderer.flyDurationSec=opts.flyDuration;
		if (typeof opts.flyKnock==='number') renderer.flyKnockMag=opts.flyKnock;
		if (typeof opts.flyKnockDur==='number') renderer.flyKnockDurSec=opts.flyKnockDur;
		if (opts.flyOrder==='reverse') renderer.flyReverse=true;
		if (opts.flyNoOvershoot===true) renderer.flyNoOvershoot=true;

		// opts.rotation: per-page settled orientation, in DEGREES for
		// readability. Stored as radians on renderer.rotation.
		if (opts.rotation)
			{
			const DEG=Math.PI/180;
			if (typeof opts.rotation.x==='number') renderer.rotation.x=opts.rotation.x*DEG;
			if (typeof opts.rotation.y==='number') renderer.rotation.y=opts.rotation.y*DEG;
			if (typeof opts.rotation.z==='number') renderer.rotation.z=opts.rotation.z*DEG;
			}

		// opts.settleAfter: seconds of no clicking before the logo eases back to
		// the pose it opened with. Read AFTER opts.rotation above, so the snapshot
		// is the page's intended pose rather than the bare 0,0,0 default.
		if (typeof opts.settleAfter==='number'&&opts.settleAfter>0)
			{
			renderer.settleAfterSec=opts.settleAfter;
			renderer.homeRotation={x:renderer.rotation.x,y:renderer.rotation.y,z:renderer.rotation.z};
			}

		// opts.lighting: per-page initial lighting values (merged into the
		// renderer's defaults). Useful when one logo needs warmer/colder/
		// stronger lighting than the global default that suits another.
		if (opts.lighting) Object.assign(renderer.lighting,opts.lighting);

		// opts.lightingAnimate: fn(t, lighting) called each frame to mutate
		// lighting over time. t is seconds since the canvas first became
		// visible. The tuner pauses this while sliders are being dragged.
		if (typeof opts.lightingAnimate==='function')
			renderer.lightingAnimate=opts.lightingAnimate;

		// In-page lighting tuner — appended once per page, attaches to the
		// most-recently-created renderer (last InitLogo3D wins if multiple).
		try
			{
			const dbg=new URLSearchParams(window.location.search).get('debug')||'';
			if (dbg.includes('light')&&!document.getElementById('logo3d-tuner'))
				MountLightingTuner(renderer);
			}
		catch (_) { /* no URLSearchParams or no window.location — skip */ }

		// Declared before the handlers below so none closes over a binding that
		// is still in its temporal dead zone at registration time.
		let running=true;
		let disposed=false;
		let rafId=0;
		let watchdogTimer=0;
		const onResize=()=>renderer.resize();
		window.addEventListener('resize',onResize);

		// Click handling: the canvas is pointer-events:none, so clicks fall through to
		// the wrapper underneath the logo. Listen on that wrapper.
		//
		// Preference order, and why the <img> is LAST RESORT ONLY: while the canvas is
		// running the <img> is opacity:0/visibility:hidden so the bitmap doesn't flash
		// through the canvas — and a visibility:hidden element receives no pointer
		// events. So binding to the <img> means no clicks at all on the happy path.
		// It stays as the fallback purely for the .show-fallback case, where both 3D
		// tiers failed, the canvas is gone and the <img> IS visible.
		//
		// '.kanlogo-hit' is the current wrapper (a <span> — see home.ejs / forums.ejs).
		// The tagName==='A' branch is kept for any page still using the old
		// <a onclick='ClickLogo()'> wrapper, which was an href-less anchor: it read as
		// a link to crawlers and assistive tech while navigating nowhere.
		const hit=imgElement.closest&&imgElement.closest('.kanlogo-hit');
		const clickTarget=hit
			?hit
			:(imgElement.parentElement&&imgElement.parentElement.tagName==='A'
				?imgElement.parentElement
				:imgElement);
		// Two click behaviors are supported:
		//   - default (opts.clickAxisCycle != true): the original toggle —
		//     swap between baseMode and 'spin'.  Pages whose intro is 'static'
		//     with baseMode:'static' get the (slightly weird but pre-existing)
		//     spin ↔ no-anim alternation.  Pages like the old home with
		//     baseMode:'flyin' get the fly-in blast on every other click.
		//   - opts.clickAxisCycle (true): each click triggers a spin, with the
		//     axis cycling x → y → z → x → ...  Home opts in here so the
		//     blast-apart fly-in is no longer reachable from a click.
		const clickAxisCycle=!!opts.clickAxisCycle;
		const prevCursor=clickTarget.style.cursor;
		clickTarget.style.cursor='pointer';
		const onClick=()=>
			{
			if (clickAxisCycle)
				{
				const axes=['x','y','z'];
				const prevAxis=renderer.spinAxis;

				// If we're mid-spin when the user clicks again, bank whatever
				// rotation the in-flight spin has produced so far into the
				// settled rotation on the OLD axis.  Without this, when we
				// reset introStartT below and switch axes the old axis's
				// groupRot drops to zero and the logo snaps back to its rest
				// pose on that axis.  Banked angle is taken mod 2π so settled
				// rotations stay bounded across many clicks.
				if (renderer.introMode==='spin'&&prevAxis)
					{
					const elapsed=(performance.now()-renderer.introStartT)/1000;
					const banked=renderer.SampleSpinAngle(elapsed);
					renderer.rotation[prevAxis]=(renderer.rotation[prevAxis]+banked)%(2*Math.PI);
					}

				// First click: spinAxis is null, indexOf returns -1, next
				// index is 0 → 'x'.  Subsequent clicks advance.
				// Cancel any settle in progress and restart the idle countdown --
				// the user is clearly still playing.
				renderer.settleStartT=null;
				renderer.lastClickT=performance.now();

				renderer.spinAxis=axes[(axes.indexOf(prevAxis)+1)%axes.length];
				renderer.introMode='spin';
				renderer.flyinOffsets=null;
				renderer.introStartT=performance.now();
				}
			else
				{
				// Pre-existing toggle: stays for callers that haven't opted
				// into the axis-cycle behavior so their UX is unchanged.
				renderer.introMode=(renderer.introMode===renderer.baseMode)?'spin':renderer.baseMode;
				renderer.flyinOffsets=null;								// re-randomize on next render() with current view extent
				renderer.introStartT=performance.now();
				}
			};
		clickTarget.addEventListener('click',onClick);

		const tick=()=>
			{
			rafId=0;
			if (!running) return;
			renderer.render();
			rafId=requestAnimationFrame(tick);
			};
		// Pause when page hidden — saves battery on background tabs.
		const onVisibility=()=>
			{
			if (disposed) return;
			if (document.hidden) running=false;
			else if (!running)
				{
				running=true;
				if (!rafId) rafId=requestAnimationFrame(tick);		// a frame still queued from before the hide carries on by itself; a second would draw every frame twice
				}
			};
		// Teardown(reveal) - the ONE way this renderer stops: Dispose(), a lost
		// context or device, the watchdog. It removes every listener it added, so
		// nothing can restart the loop afterwards -- the visibilitychange handler
		// used to do exactly that, rAF-ing a dead canvas forever after a teardown.
		const Teardown=(reveal)=>
			{
			if (disposed) return;
			disposed=true;
			running=false;
			if (rafId) cancelAnimationFrame(rafId);
			rafId=0;
			clearTimeout(watchdogTimer);
			window.removeEventListener('resize',onResize);
			document.removeEventListener('visibilitychange',onVisibility);
			clickTarget.removeEventListener('click',onClick);
			clickTarget.style.cursor=prevCursor;
			try { backend.dispose(); } catch (_) { /* context already gone */ }
			if (gl) LoseContext(gl);								// free it now, not at GC (the context cap, see LoseContext)
			if (gpu) ReleaseLogoGPU(gpu.device);					// the last renderer on the page destroys the shared device
			canvas.remove();
			if (window.__lastLogo3DRenderer===renderer) window.__lastLogo3DRenderer=null;
			if (reveal) revealFallback();
			};

		// A lost context leaves the canvas blank FOREVER, and the placeholder is
		// hidden by now, so the page shows nothing at all -- no error, no
		// fallback. Degrade to the flat SVG instead. (Nothing in the repo
		// handled contextlost before this.)
		if (gl)
			canvas.addEventListener('webglcontextlost',(ev)=>
				{
				ev.preventDefault();								// preventDefault is what makes restore possible at all
				if (disposed) return;								// our own LoseContext() in Teardown
				console.warn("[Logo3D] WebGL context lost — revealing SVG fallback.");
				Teardown(true);
				});
		// The same for the WebGPU tier: a device lost (driver reset, GPU removed,
		// or a validation error on the first frame, which draws nothing and says
		// nothing) reveals the bitmap rather than leaving a blank canvas.
		if (gpu)
			{
			gpu.device.lost.then((info)=>
				{
				if (disposed||(info&&info.reason==='destroyed')) return;
				console.warn(`[Logo3D] WebGPU device lost (${(info&&info.message)||'no reason given'}) — revealing SVG fallback.`);
				Teardown(true);
				});
			backend.onFatal=(err)=>
				{
				if (disposed) return;
				console.warn("[Logo3D] WebGPU frame rejected — revealing SVG fallback:",err&&err.message);
				Teardown(true);
				};
			}

		// Watchdog: render() refuses to draw until the placeholder is laid out
		// AND its parent is visible (the `targetReady` gate). Those depend on
		// things outside this module -- a remote image finishing its download, a
		// caller adding a reveal class -- and if either never happens we sit
		// here drawing nothing while the placeholder stays hidden. That is the
		// "logo just isn't there" failure. Give it a generous window (the logo
		// intro itself only runs 4s) and then degrade instead of vanishing.
		// (Armed only now, once a canvas exists: the WebGPU device request
		// before it has its own deadline, LOGO_GPU_TIMEOUT_MS.)
		const READY_TIMEOUT_MS=8000;
		const armReadyWatchdog=()=>{ watchdogTimer=setTimeout(()=>
			{
			if (renderer.introStartT!==null) return;					// drew fine
			if (disposed||!canvas.isConnected) return;				// already torn down
			// Do NOT count time the page was hidden. A backgrounded tab
			// (cmd-click, session restore, another window in front) has its
			// rAF throttled to roughly 1 fps, so the renderer's warm-up gate
			// -- which wants three consecutive frames at >=30fps before it
			// latches -- legitimately cannot pass. Tearing down here would
			// permanently drop a perfectly healthy page to the flat SVG for
			// the very common case of opening the site in a background tab.
			// Re-arm instead, and let the real failure (a placeholder that
			// never gets a usable box) trip it once the page is actually on
			// screen.
			if (document.hidden || !running) { armReadyWatchdog(); return; }
			console.warn(`[Logo3D] placeholder never became ready within ${READY_TIMEOUT_MS}ms of visible time — revealing SVG fallback.`);
			Teardown(true);
			},READY_TIMEOUT_MS); };
		armReadyWatchdog();

		rafId=requestAnimationFrame(tick);
		document.addEventListener('visibilitychange',onVisibility);

		console.debug(`[Logo3D] active on ${backend.tier}: ${mesh.N} verts, ${mesh.count/3} tris`);	// debug (not log): dev-only diagnostic, hidden from regular users at default DevTools log level
		// Diagnostic handle for devtools / preview harnesses. Read-only;
		// poking renderer state from the outside isn't supported.
		window.__lastLogo3DRenderer=renderer;
		return({tier:backend.tier,renderer,canvas,Dispose:()=>Teardown(false)});
		}
	catch (err)
		{
		console.warn("[Logo3D] init failed, keeping SVG:",err);
		if (backend) { try { backend.dispose(); } catch (_) { /* context already gone */ } }
		if (gl) LoseContext(gl);
		if (gpu) ReleaseLogoGPU(gpu.device);
		if (canvas) canvas.remove();
		revealFallback();
		return false;
		}
	}
